import { normalize } from 'normalizr'
import { Schemas } from '@redux/schema'
import { updateEntities } from '@redux/entities'

import api from '@functions/api'

import type { GeoCountryModel } from '@models/types'

import type { AppDispatch } from '@redux/store'
import type {
  Action, EntityOptions, Errors, FetchRequestPayload, RootReducerState, UpdateParams,
} from '@redux/modules/types'

import { handleError } from '../utils'

const FETCH_REQUEST = 'realbase/geoCountry/FETCH_REQUEST'
const FETCH_SUCCESS = 'realbase/geoCountry/FETCH_SUCCESS'
const FETCH_FAILURE = 'realbase/geoCountry/FETCH_FAILURE'

const CREATE_REQUEST = 'realbase/geoCountry/CREATE_REQUEST'
const CREATE_SUCCESS = 'realbase/geoCountry/CREATE_SUCCESS'
const CREATE_FAILURE = 'realbase/geoCountry/CREATE_FAILURE'

const UPDATE_REQUEST = 'realbase/geoCountry/UPDATE_REQUEST'
const UPDATE_SUCCESS = 'realbase/geoCountry/UPDATE_SUCCESS'
const UPDATE_FAILURE = 'realbase/geoCountry/UPDATE_FAILURE'

export type GeoCountryModuleState = {
  creating: boolean,
  errors: Errors,
  loaded: boolean,
  loadedForKeys: string[],
  loadedIds: number[],
  loading: boolean,
  updating: boolean,
}

// Initial State
const initialState: GeoCountryModuleState = {
  creating: false,
  errors: [],
  loaded: false,
  loadedForKeys: [],
  loadedIds: [],
  loading: false,
  updating: false,
}

// Actions
export function fetchRequest(payload: FetchRequestPayload = {}): Action {
  return {
    type: FETCH_REQUEST,
    loadedForKeys: payload.loadedForKeys,
    loadedIds: payload.loadedIds,
  }
}

export function fetchSuccess(): Action {
  return {
    type: FETCH_SUCCESS,
  }
}

export function fetchFailure(errors = []): Action {
  return {
    type: FETCH_FAILURE,
    errors,
  }
}

export function createRequest(): Action {
  return {
    type: CREATE_REQUEST,
  }
}

export function createSuccess(): Action {
  return {
    type: CREATE_SUCCESS,
  }
}

export function createFailure(errors: Errors = []): Action {
  return {
    type: CREATE_FAILURE,
    errors,
  }
}

export function updateRequest(): Action {
  return {
    type: UPDATE_REQUEST,
  }
}

export function updateSuccess(): Action {
  return {
    type: UPDATE_SUCCESS,
  }
}

export function updateFailure(errors: Errors = []): Action {
  return {
    type: UPDATE_FAILURE,
    errors,
  }
}

// Action Creators
export function loadGeoCountries(options: EntityOptions = {}) {
  const { entityKey } = options

  return (dispatch: AppDispatch, getState: () => RootReducerState) => {
    const loadedForKeys = [...getState().geoCountries.loadedForKeys]

    if (entityKey && !loadedForKeys.includes(entityKey)){
      loadedForKeys.push(entityKey)
    }

    dispatch(fetchRequest({ loadedForKeys }))

    const promise = api('/geo_countries.json', options)
      .then(({ data }: { data: GeoCountryModel[] }) => {
        const normalizedJson = normalize(data, Schemas.GEO_COUNTRY_ARRAY)
        // @ts-expect-error TS2345
        dispatch(updateEntities(normalizedJson))
        dispatch(fetchSuccess())

        return { success: true as const, data }
      })
      .catch((data) => {
        const errors = handleError(data)
        // @ts-expect-error TS2345
        dispatch(fetchFailure(errors))

        return { success: false as const, errors }
      })

    return promise
  }
}

export function loadGeoCountry(geoCountryId: number, options: EntityOptions = {}) {
  const { entityKey } = options

  return (dispatch: AppDispatch, getState: () => RootReducerState) => {
    const state = getState()
    const loadedIds = [...state.geoCountries.loadedIds]
    const loadedForKeys = [...state.geoCountries.loadedForKeys]

    if (geoCountryId && !loadedIds.includes(geoCountryId)) loadedIds.push(geoCountryId)
    if (entityKey) loadedForKeys.push(entityKey)

    dispatch(fetchRequest({ loadedForKeys, loadedIds }))

    const promise = api(`/geo_countries/${geoCountryId}.json`, options)
      .then(({ data }: { data: GeoCountryModel }) => {
        const normalizedJson = normalize(data, Schemas.GEO_COUNTRY_ARRAY)
        // @ts-expect-error TS2345
        dispatch(updateEntities(normalizedJson))
        dispatch(fetchSuccess())

        return { success: true as const, data }
      })
      .catch((data) => {
        const errors = handleError(data)
        // @ts-expect-error TS2345
        dispatch(fetchFailure(errors))

        return { success: false as const, errors }
      })

    return promise
  }
}

export function createGeoCountry(geoCountry: Partial<GeoCountryModel>, options: EntityOptions = {}) {
  const config = {
    method: 'POST',
    data: JSON.stringify({
      geo_country: geoCountry,
    }),
  }

  return (dispatch: AppDispatch) => {
    dispatch(createRequest())

    const promise = api('/geo_countries.json', options, config)
      .then(({ data }: { data: GeoCountryModel }) => {
        const normalizedJson = normalize(data, Schemas.GEO_COUNTRY)
        // @ts-expect-error TS2345
        dispatch(updateEntities(normalizedJson))
        dispatch(createSuccess())

        return { success: true as const, data }
      })
      .catch((data) => {
        const errors = handleError(data)
        dispatch(createFailure(errors))

        return { success: false as const, errors }
      })

    return promise
  }
}

export function updateGeoCountry(geoCountry: UpdateParams<GeoCountryModel>, options: EntityOptions = {}) {
  const config = {
    method: 'PUT',
    data: JSON.stringify({
      geo_country: geoCountry,
    }),
  }

  return (dispatch: AppDispatch) => {
    dispatch(updateRequest())

    const promise = api(`/geo_countries/${geoCountry.id}.json`, options, config)
      .then(({ data }: { data: GeoCountryModel }) => {
        const normalizedJson = normalize(data, Schemas.GEO_COUNTRY)
        // @ts-expect-error TS2345
        dispatch(updateEntities(normalizedJson))
        dispatch(updateSuccess())

        return { success: true as const, data }
      })
      .catch((data) => {
        const errors = handleError(data)
        dispatch(updateFailure(errors))

        return { success: false as const, errors }
      })

    return promise
  }
}

// Reducer
export default function reducer(
  state: GeoCountryModuleState = initialState,
  action: Action = { type: '' },
): GeoCountryModuleState {
  switch (action.type){
    case FETCH_REQUEST:
      return {
        ...state,
        loadedForKeys: action.loadedForKeys || state.loadedForKeys,
        loadedIds: action.loadedIds || state.loadedIds,
        loading: true,
      }
    case FETCH_SUCCESS:
      return {
        ...state,
        loading: false,
        loaded: true,
        errors: [],
      }
    case FETCH_FAILURE:
      return {
        ...state,
        loading: false,
        loaded: false,
        errors: action.errors || [],
      }
    case CREATE_REQUEST:
      return {
        ...state,
        creating: true,
      }
    case CREATE_SUCCESS:
      return {
        ...state,
        creating: false,
        errors: [],
      }
    case CREATE_FAILURE:
      return {
        ...state,
        creating: false,
        errors: action.errors || [],
      }
    case UPDATE_REQUEST:
      return {
        ...state,
        updating: true,
      }
    case UPDATE_SUCCESS:
      return {
        ...state,
        updating: false,
        errors: [],
      }
    case UPDATE_FAILURE:
      return {
        ...state,
        updating: false,
        errors: action.errors || [],
      }
    default:
      return state
  }
}
